import {
  type HttpRequestFn,
  type HttpResponse,
  type UseHttpOptions,
  useHttp,
} from '@tundr/http';
import {
  isPermissionGuardEnabled,
  isRolesUiEnabled,
} from '../utils/feature-flag.ts';
import type { GetPermissionsResponse } from './permissions.api.types.ts';

/** Relative to the HR app `VITE_API_BASE_URL` (already includes `/api/hr`). */
export const PERMISSIONS_ENDPOINT = 'me/permissions';

/** Polling cadence for `useGetLoggedHrPermission`. Currently 1 hour. */
export const PERMISSIONS_POLLING_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Empty stub returned when both authorization flags are OFF.
 * Prevents BFF calls during the deploy-before-BFF window. The endpoint is
 * still fetched when the roles UI is enabled, so the logged user's role is
 * available even while the permission guard stays off.
 */
const emptyPermissionsResponse = (): HttpResponse<GetPermissionsResponse> =>
  ({
    data: { permissions: [] },
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {},
  }) as unknown as HttpResponse<GetPermissionsResponse>;

export const getLoggedHrPermission: HttpRequestFn<
  GetPermissionsResponse,
  void
> = (http) => async () => {
  if (!isPermissionGuardEnabled() && !isRolesUiEnabled()) {
    return emptyPermissionsResponse();
  }
  return http.get<GetPermissionsResponse, HttpResponse<GetPermissionsResponse>>(
    PERMISSIONS_ENDPOINT,
  );
};

export const useGetLoggedHrPermission = (
  options: UseHttpOptions<GetPermissionsResponse, void> = {},
) =>
  useHttp(getLoggedHrPermission, {
    pollingInterval: PERMISSIONS_POLLING_INTERVAL_MS,
    ...options,
  });
